import { useEffect, useRef } from 'react'
import { GripVertical } from 'lucide-react'
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine'
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { useTranslation } from 'react-i18next'
import type { ReactNode } from 'react'
import type { StatusDocument } from '@/lib/db/rxdb/schemas/status'
import { TableCell, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'

export type StatusReorderEvent = {
  sourceId: string
  targetId: string
  dropPosition: 'before' | 'after'
}

export type StatusDropIndicator = {
  id: string
  position: 'before' | 'after'
}

export interface StatusColumn {
  id: string
  cell: (status: StatusDocument) => ReactNode
  className?: string
}

type StatusDragData = {
  type: 'status-row'
  id: string
  progressType: string
}

function extractStatusDragData(
  data: Record<string | symbol, unknown> | undefined,
): StatusDragData | null {
  if (!data) return null
  if (data['type'] !== 'status-row') return null
  const id = data['id']
  const progressType = data['progressType']
  if (typeof id !== 'string' || typeof progressType !== 'string') return null
  return { type: 'status-row', id, progressType }
}

interface StatusRowProps {
  status: StatusDocument
  columns: Array<StatusColumn>
  canDrag: boolean
  draggingId: string | null
  onDragStart: (id: string) => void
  onDragEnd: () => void
  onReorder: (event: StatusReorderEvent) => void
  dropIndicator: StatusDropIndicator | null
  onDropIndicatorChange: (next: StatusDropIndicator | null) => void
}

export function StatusRow({
  status,
  columns,
  canDrag,
  draggingId,
  onDragStart,
  onDragEnd,
  onReorder,
  dropIndicator,
  onDropIndicatorChange,
}: StatusRowProps) {
  const { t } = useTranslation('settings')
  const rowRef = useRef<HTMLTableRowElement>(null)
  const handleRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!canDrag) return
    const element = rowRef.current
    const dragHandle = handleRef.current
    if (!element || !dragHandle) return

    return combine(
      draggable({
        element,
        dragHandle,
        getInitialData: () => ({
          type: 'status-row',
          id: status.id,
          progressType: status.progressType,
        }),
        onDragStart: () => {
          onDragStart(status.id)
          onDropIndicatorChange(null)
        },
        onDrop: () => {
          onDragEnd()
          onDropIndicatorChange(null)
        },
      }),
      dropTargetForElements({
        element,
        canDrop: ({ source }) => {
          const data = extractStatusDragData(source.data)
          if (!data) return false
          if (data.id === status.id) return false
          // Restrict to same Progress Type bucket
          return data.progressType === status.progressType
        },
        getData: () => ({
          type: 'status-row',
          id: status.id,
          progressType: status.progressType,
        }),
        onDropTargetChange: ({ location, source, self }) => {
          const data = extractStatusDragData(source.data)
          if (
            !data ||
            data.id === status.id ||
            data.progressType !== status.progressType
          ) {
            if (dropIndicator?.id === status.id) {
              onDropIndicatorChange(null)
            }
            return
          }

          const rect = (self.element as HTMLElement).getBoundingClientRect()
          const pointerY = location.current.input.clientY
          const shouldPlaceAfter = pointerY > rect.top + rect.height / 2

          onDropIndicatorChange({
            id: status.id,
            position: shouldPlaceAfter ? 'after' : 'before',
          })
        },
        onDragLeave: () => {
          if (dropIndicator?.id === status.id) {
            onDropIndicatorChange(null)
          }
        },
        onDrop: ({ location, source, self }) => {
          const data = extractStatusDragData(source.data)
          onDropIndicatorChange(null)
          if (
            !data ||
            data.id === status.id ||
            data.progressType !== status.progressType
          ) {
            return
          }

          const innermostTarget = location.current.dropTargets[0]
          if (innermostTarget?.element !== self.element) return

          const rect = (self.element as HTMLElement).getBoundingClientRect()
          const pointerY = location.current.input.clientY
          const shouldPlaceAfter = pointerY > rect.top + rect.height / 2

          onReorder({
            sourceId: data.id,
            targetId: status.id,
            dropPosition: shouldPlaceAfter ? 'after' : 'before',
          })
        },
      }),
    )
  }, [
    canDrag,
    status.id,
    status.progressType,
    onDragStart,
    onDragEnd,
    onReorder,
    onDropIndicatorChange,
    dropIndicator?.id,
  ])

  const isDragging = draggingId === status.id
  const showIndicatorBefore =
    dropIndicator?.id === status.id && dropIndicator.position === 'before'
  const showIndicatorAfter =
    dropIndicator?.id === status.id && dropIndicator.position === 'after'

  return (
    <TableRow
      ref={rowRef}
      className={cn(
        'group relative',
        isDragging && 'opacity-40',
        (showIndicatorBefore || showIndicatorAfter) && 'bg-primary/5',
      )}
    >
      <TableCell className="relative w-8 p-0 align-middle">
        {showIndicatorBefore && (
          <div className="pointer-events-none absolute inset-x-0 -top-px z-10 h-0.5 bg-primary" />
        )}
        {showIndicatorAfter && (
          <div className="pointer-events-none absolute inset-x-0 -bottom-px z-10 h-0.5 bg-primary" />
        )}
        {canDrag ? (
          <button
            type="button"
            ref={handleRef}
            aria-label={t('status_management.drag_handle_label', {
              name: status.name,
            })}
            className={cn(
              'flex size-6 items-center justify-center rounded text-muted-foreground/60 transition mx-auto',
              'opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
              'hover:cursor-grab active:cursor-grabbing hover:text-muted-foreground',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
              isDragging && 'opacity-100 text-primary cursor-grabbing',
            )}
          >
            <GripVertical className="size-3.5" />
          </button>
        ) : null}
      </TableCell>
      {columns.map((col) => (
        <TableCell key={col.id} className={col.className}>
          {col.cell(status)}
        </TableCell>
      ))}
    </TableRow>
  )
}
